/**
 * Booking Widget — replaces Cowlendar booking flow.
 * Renders a 2-screen booking experience on product pages:
 *   Screen 1: Slot selection (date pills + time slots)
 *   Screen 2: Guest details + Add to Cart
 *
 * Communicates with the Rails backend via App Proxy at /a/proxy/bookings/*
 * Translations loaded from window.bookingStrings (metaobject-based, see booking-translations.liquid)
 */

if (!customElements.get('booking-widget')) {
  customElements.define('booking-widget', class BookingWidget extends HTMLElement {
    constructor() {
      super();
      this.proxyBase = '/a/proxy/bookings';
      this._t = window.bookingStrings || {};
      this.variantId = null;
      this.eventData = null;
      this.variantData = null;
      this.availableDays = [];
      this.selectedDate = null;
      this.selectedSlot = null;
      this.guestCount = 1;
      this.childrenAges = [];
      this.screen = 'slots'; // 'slots' or 'details'
      this.calendarOpen = false;
      this.calendarMonth = null;
      this.calendarSlots = {};
      this.firstMonth = null;
      this.lastMonth = null;
      this.isLoading = false;
      this.requestedSlotId = null;
      this.requestedSlotDate = null;
      this.privateEvent = false;
      this.bookingKey = null;
      this.accessDenied = false;
    }

    t(key, fallback, vars) {
      let str = this._t[key] || fallback || key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(`[${k}]`, v);
        });
      }
      return str;
    }

    connectedCallback() {
      const params = new URLSearchParams(window.location.search);
      const requestedSlotId = Number.parseInt(params.get('booking_slot'), 10);
      const requestedSlotDate = params.get('booking_date');
      this.requestedSlotId = Number.isInteger(requestedSlotId) && requestedSlotId > 0 ? requestedSlotId : null;
      this.requestedSlotDate = /^\d{4}-\d{2}-\d{2}$/.test(requestedSlotDate || '') ? requestedSlotDate : null;
      this.variantId = this.dataset.initialVariantId || null;
      this.variantPrice = this.dataset.initialVariantPrice ? parseInt(this.dataset.initialVariantPrice) : null;
      this.credit = null;
      this.privateEvent = this.dataset.privateEvent === 'true';
      this.bookingKey = params.get('key') || null;

      this.listenForVariantChanges();
      if (this.variantId) {
        this.loadAvailability();
      }
    }

    switchVariant(variant) {
      if (!variant) return;
      this.variantId = variant.id;
      this.variantPrice = variant.price;
      this.reset();
      this.loadAvailability();
    }

    listenForVariantChanges() {
      // Fallback: listen for pub/sub in case product-info doesn't call switchVariant
      if (typeof subscribe === 'function') {
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
          const variant = event?.data?.variant;
          if (variant?.id) {
            this.switchVariant(variant);
          }
        });
      }
    }

    updateDisplayedPrice() {
      if (!this.variantPrice || !this.variantData) return;
      const cost = this.variantData.capacity_cost || 1;
      const minGuests = this.eventData?.min_guests || cost;
      const guests = Math.max(this.guestCount, minGuests);
      const qty = Math.ceil(guests / cost);
      const totalCents = this.variantPrice * qty;
      const priceEl = document.querySelector('.price-item--regular:not(s)');
      if (priceEl) priceEl.textContent = this.formatPrice(totalCents / 100);
      this.renderPriceNote(totalCents / 100);
    }

    renderPriceNote(total) {
      const info = this.closest('.product__info-container');
      const anchor = info?.querySelector('.product__tax') || info?.querySelector('[id^="price-"]');
      let note = info?.querySelector('.booking-widget__price-note');
      const text = this.priceNoteText(total);

      if (!anchor || !text) {
        note?.remove();
        return;
      }
      if (!note) {
        note = document.createElement('p');
        note.className = 'booking-widget__price-note';
        anchor.after(note);
      }
      note.innerHTML = text;
    }

    priceNoteText(total) {
      const rate = this.eventData?.bundle_credit_rate;
      if (!rate) return null;

      if (this.credit) {
        const credit = `<strong>${this.formatPrice(this.credit.credit)}</strong>`;
        return this.credit.credit >= total
          ? this.t('credit_covered', 'You have [credit] Playroom credit, so this one is covered.', { credit })
          : this.t('credit_partial', 'You have [credit] Playroom credit to put towards this.', { credit });
      }

      if (!this._t.bundle_url) return null;
      const price = `<strong>${this.formatPrice(Math.ceil(Math.round(total * rate * 100) / 100))}</strong>`;
      const bundle = `<a href="${this._t.bundle_url}">${this.t('bundle_from_link', 'Playroom bundle')}</a>`;
      return this.t('bundle_from_price', 'or from [price] with a [bundle]', { price, bundle });
    }

    reset() {
      this.selectedDate = null;
      this.selectedSlot = null;
      this.guestCount = 1;
      this.childrenAges = [];
      this.screen = 'slots';
      this.calendarOpen = false;
      this.calendarMonth = null;
      this.calendarSlots = {};
      this.firstMonth = null;
      this.lastMonth = null;
      this.singleSlotMode = false;
      this.credit = null;
      this.accessDenied = false;
    }

    keyParam() {
      return this.bookingKey ? `&key=${encodeURIComponent(this.bookingKey)}` : '';
    }

    applyPrivateEventDetails() {
      if (!this.privateEvent) return;
      const info = this.closest('.product__info-container');
      if (!this.eventData) {
        if (info) info.classList.remove('private-event--unlocked');
        return;
      }
      document.querySelectorAll('.product__title h1, .product__title h2').forEach(el => {
        el.textContent = this.eventData.name;
      });
      document.title = this.eventData.name;
      if (info) info.classList.add('private-event--unlocked');
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    ageRange() {
      const event = this.eventData || {};
      const min = Number.isFinite(event.age_min) ? event.age_min : 0;
      const max = Number.isFinite(event.age_max) ? event.age_max : 12;
      const step = event.age_step > 0 ? event.age_step : 1;
      return { min, max, step };
    }

    defaultAge() {
      const { min, max, step } = this.ageRange();
      const onStep = Math.round((min + Math.round((1 - min) / step) * step) * 10) / 10;
      return Math.min(max, Math.max(min, onStep));
    }

    stepAge(age, direction) {
      const { min, max, step } = this.ageRange();
      const next = Math.round((age + direction * step) * 10) / 10;
      return Math.min(max, Math.max(min, next));
    }

    // ──────── API Calls ────────

    async loadAvailability() {
      if (!this.variantId) return;

      // Cancel any in-flight requests
      if (this._availabilityController) this._availabilityController.abort();
      this._availabilityController = new AbortController();
      const requestedVariant = this.variantId;

      this.isLoading = true;
      this.render();

      try {
        const res = await fetch(`${this.proxyBase}/availability?variant_id=${this.variantId}&locale=${encodeURIComponent(document.documentElement.lang || 'en')}${this.keyParam()}`, { signal: this._availabilityController.signal });
        this.accessDenied = res.status === 403;
        if (this.accessDenied) {
          this.eventData = null;
          this.applyPrivateEventDetails();
        }
        if (!res.ok) throw new Error('Failed to load availability');

        // Stale response check: variant may have changed while fetching
        if (this.variantId !== requestedVariant) return;

        const data = await res.json();
        this.eventData = data.event;
        this.variantData = data.variant;
        this.availableDays = data.available_days;
        this.firstMonth = data.first_month;
        this.lastMonth = data.last_month;
        const cost = data.variant.capacity_cost || 1;
        const minG = data.event.min_guests || cost;
        this.guestCount = Math.max(minG, Math.ceil(this.guestCount / cost) * cost);
        this.updateDisplayedPrice();
        this.applyPrivateEventDetails();

        if (data.event.credit_enabled && document.querySelector('meta[name="customer-id"]')?.content) {
          this.loadCredit();
        }

        const slotPreselected = await this.preselectRequestedSlot();

        if (!slotPreselected && this.availableDays.length > 0) {
          this.selectedDate = this.availableDays[0].date;
        }

        // Count total slots across all days
        const totalSlots = this.availableDays.reduce((sum, day) => sum + day.slots.length, 0);
        this.singleSlotMode = totalSlots === 1;

        // Auto-select if only one slot available
        if (!slotPreselected && this.singleSlotMode) {
          const onlySlot = this.availableDays[0].slots[0];
          this.selectedSlot = onlySlot;
          this.screen = 'details';
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Booking availability error:', err);
          this.availableDays = [];
        }
      }

      this.isLoading = false;
      this.render();
    }

    async loadCalendarMonth(yearMonth, shouldRender = true) {
      if (!this.eventData) return;

      if (this._calendarController) this._calendarController.abort();
      this._calendarController = new AbortController();
      const requestedVariant = this.variantId;

      try {
        const res = await fetch(`${this.proxyBase}/slots?variant_id=${this.variantId}&month=${yearMonth}${this.keyParam()}`, { signal: this._calendarController.signal });
        if (!res.ok) return;
        if (this.variantId !== requestedVariant) return;

        const data = await res.json();
        this.calendarSlots = {};
        data.days.forEach(day => {
          this.calendarSlots[day.date] = day.slots;
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Calendar load error:', err);
      }
      if (shouldRender) this.render();
    }

    async preselectRequestedSlot() {
      if (!this.requestedSlotId) return false;

      let targetDate = null;
      let targetSlot = null;

      this.availableDays.some(day => {
        const slot = day.slots.find(candidate => candidate.id === this.requestedSlotId);
        if (!slot) return false;

        targetDate = day.date;
        targetSlot = slot;
        return true;
      });

      if (!targetSlot && this.requestedSlotDate) {
        this.calendarMonth = this.requestedSlotDate.slice(0, 7);
        await this.loadCalendarMonth(this.calendarMonth, false);
        targetSlot = (this.calendarSlots[this.requestedSlotDate] || [])
          .find(candidate => candidate.id === this.requestedSlotId);
        targetDate = targetSlot ? this.requestedSlotDate : null;
      }

      if (!targetSlot) return false;

      this.selectedDate = targetDate;
      this.selectedSlot = targetSlot;
      this.screen = 'details';
      this.requestedSlotId = null;
      this.requestedSlotDate = null;
      return true;
    }

    async loadCredit() {
      try {
        const res = await fetch(`${this.proxyBase}/credit`);
        if (!res.ok) return;
        const data = await res.json();
        this.credit = data.credit > 0 ? data : null;
        this.updateDisplayedPrice();
        this.render();
      } catch (err) {
        // Not logged in or no credit — silently ignore
        this.credit = null;
      }
    }

    async reserveSlot() {
      if (!this.selectedSlot || !this.variantData) return;

      // Pre-check: enough remaining capacity for this booking?
      const capacityNeeded = this.guestCount * this.variantData.capacity_cost;
      if (capacityNeeded > this.selectedSlot.capacity_remaining) {
        const available = Math.floor(this.selectedSlot.capacity_remaining / this.variantData.capacity_cost);
        const msg = available > 0
          ? `Only ${available} spot${available === 1 ? '' : 's'} available for this slot. Please reduce the number of guests.`
          : 'This slot is fully booked. Please select a different time.';
        if (window.MontisNotification) {
          window.MontisNotification.error(msg);
        } else {
          alert(msg);
        }
        return;
      }

      this.isLoading = true;
      this.render();

      try {
        const res = await fetch(`${this.proxyBase}/reserve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_slot_id: this.selectedSlot.id,
            event_variant_id: this.variantData.id,
            guest_count: this.guestCount,
            children_ages: this.childrenAges,
            key: this.bookingKey
          })
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || this.t('reservation_failed', 'Failed to reserve slot'));
        }

        const data = await res.json();
        await this.addToCart(data.booking_token, data.expires_at);
      } catch (err) {
        console.error('Reserve error:', err);
        if (window.MontisNotification) {
          window.MontisNotification.error(err.message || this.t('reservation_failed', 'Failed to reserve slot'));
        }
      }

      this.isLoading = false;
      this.render();
    }

    async bookWithCredit() {
      if (!this.selectedSlot || !this.variantData || !this.credit) return;

      this.isLoading = true;
      this.render();

      try {
        const cost = this.variantData.capacity_cost || 1;
        const cartQty = Math.ceil(this.guestCount / cost);
        const unitPrice = (this.variantPrice || 0) / 100;

        const res = await fetch(`${this.proxyBase}/book_with_credit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_slot_id: this.selectedSlot.id,
            event_variant_id: this.variantData.id,
            guest_count: this.guestCount,
            children_ages: this.childrenAges,
            unit_price: unitPrice,
            key: this.bookingKey
          })
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || this.t('credit_failed', 'Failed to book with credit'));
        }

        const data = await res.json();

        if (window.MontisNotification) {
          window.MontisNotification.success(this.t('credit_booked', 'Booking confirmed! [amount] deducted from your credit.', { amount: this.formatPrice(data.amount_deducted) }));
        }

        // Refresh credit balance
        this.credit.credit -= data.amount_deducted;
        this.reset();
        this.loadAvailability();
      } catch (err) {
        console.error('Book with credit error:', err);
        if (window.MontisNotification) {
          window.MontisNotification.error(err.message || this.t('credit_failed', 'Failed to book with credit'));
        }
      }

      this.isLoading = false;
      this.render();
    }

    async addToCart(bookingToken, expiresAt) {
      const slot = this.selectedSlot;
      const dateStr = this.formatDateLong(this.selectedDate);
      const timeStr = `${slot.start_time} - ${slot.end_time}`;
      const agesStr = this.childrenAges.length > 0 ? this.childrenAges.join(', ') : '';

      const properties = {
        '_booking_token': bookingToken,
        '_expires_at': expiresAt,
        'Date': dateStr,
        'Time': timeStr,
      };

      if (this.privateEvent && this.eventData?.name) {
        properties['Event'] = this.eventData.name;
      }

      if (agesStr) {
        properties['Children Ages'] = agesStr;
      }

      const cartQuantity = Math.ceil(this.guestCount / this.variantData.capacity_cost);
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const formData = new FormData();
      formData.append('id', parseInt(this.variantId));
      formData.append('quantity', cartQuantity);
      Object.entries(properties).forEach(([k, v]) => formData.append(`properties[${k}]`, v));

      if (cart) {
        const sections = cart.getSectionsToRender().map(s => s.id);
        console.log('[booking-cart] Cart element:', cart.tagName, 'sections:', sections);
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      console.log('[booking-cart] POST', window.routes.cart_add_url, 'id:', this.variantId, 'qty:', cartQuantity);

      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });

      const response = await res.json();
      console.log('[booking-cart] Response status:', res.status, 'keys:', Object.keys(response), 'has sections:', !!response.sections);
      if (response.sections) {
        console.log('[booking-cart] Section keys:', Object.keys(response.sections));
      }
      console.log('[booking-cart] Response key:', response.key);

      if (response.status) {
        console.error('[booking-cart] Cart error:', response.status, response.description);
        throw new Error(response.description || 'Failed to add to cart');
      }

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'booking-widget',
          productVariantId: this.variantId,
          cartData: response,
        });
      }

      // Apply discount code if customer has partial credit
      if (this.credit?.discount_code) {
        try {
          await fetch(`/discount/${this.credit.discount_code}`, { method: 'GET' });
        } catch (e) {
          // Silently fail — discount will need to be applied manually
        }
      }

      if (cart) {
        console.log('[booking-cart] Calling renderContents, cart type:', cart.constructor.name);
        cart.renderContents(response);
      } else {
        window.location = window.routes.cart_url;
        return;
      }

      this.reset();
      this.loadAvailability();
    }

    // ──────── Rendering ────────

    render() {
      if (this.isLoading && !this.eventData) {
        this.innerHTML = `<div class="booking-widget__loading">${this.t('loading', 'Loading availability...')}</div>`;
        return;
      }

      if (this.accessDenied) {
        this.innerHTML = `<div class="booking-widget__empty">${this.t('invitation_only', 'This event is by invitation only. Please use the link you were sent to book your spot.')}</div>`;
        return;
      }

      if (!this.eventData || !this.variantData) {
        this.innerHTML = '';
        return;
      }

      if (this.screen === 'details') {
        this.renderDetailsScreen();
      } else {
        this.renderSlotScreen();
      }
    }

    renderSlotScreen() {
      const slotsForDate = this.getSlotsForDate(this.selectedDate);

      let html = '<div class="booking-widget">';

      if (this.privateEvent && this.eventData.description) {
        html += `<div class="booking-widget__description">${this.escapeHtml(this.eventData.description).replace(/\n/g, '<br>')}</div>`;
      }

      // Date pills
      html += '<div class="booking-widget__dates">';
      this.availableDays.forEach(day => {
        const selected = day.date === this.selectedDate ? ' is-selected' : '';
        html += `<button class="booking-widget__date-pill${selected}" data-action="select-date" data-date="${day.date}">
          ${this.formatDateShort(day.date)}
        </button>`;
      });
      const calToggleLabel = this.calendarOpen
        ? this.t('hide_calendar', 'Hide calendar')
        : this.t('view_calendar', 'Calendar');
      html += `<button class="booking-widget__calendar-toggle${this.calendarOpen ? ' is-active' : ''}" data-action="toggle-calendar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        ${calToggleLabel}
      </button>`;
      html += '</div>';

      // Calendar (expandable)
      if (this.calendarOpen) {
        html += this.renderCalendar();
      }

      // Time slots
      if (this.availableDays.length === 0 && !this.calendarOpen) {
        html += `<div class="booking-widget__empty">${this.t('no_upcoming_slots', 'No dates are available to book yet.')}</div>`;
      } else if (slotsForDate.length > 0) {
        html += `<div class="booking-widget__section-heading">${this.t('select_slot', 'Select a time slot')}</div>`;
        html += '<div class="booking-widget__slots">';
        slotsForDate.forEach(slot => {
          const selected = this.selectedSlot && this.selectedSlot.id === slot.id ? ' is-selected' : '';
          const remaining = slot.capacity_remaining;
          const lowClass = remaining <= 3 ? ' is-low' : '';
          const spotsText = remaining === 1
            ? this.t('spot_left', '[count] spot left', { count: remaining })
            : this.t('spots_left', '[count] spots left', { count: remaining });
          html += `<button class="booking-widget__slot${selected}" data-action="select-slot" data-slot='${JSON.stringify(slot)}'>
            <span class="booking-widget__slot-time">${slot.start_time} - ${slot.end_time}</span>
            ${this.eventData.hide_capacity
              ? (remaining > 0
                ? `<span class="booking-widget__slot-remaining"><span class="booking-widget__dot booking-widget__dot--green"></span> ${this.t('slot_available', 'Available')}</span>`
                : `<span class="booking-widget__slot-remaining is-full"><span class="booking-widget__dot booking-widget__dot--red"></span> ${this.t('slot_full', 'Full')}</span>`)
              : `<span class="booking-widget__slot-remaining${lowClass}">${spotsText}</span>`}
            ${slot.village_fit ? `<span class="booking-widget__slot-village"><span class="booking-widget__slot-village-chip">💛 ${this.t('village_fit', 'Looks like a good fit for your family')}</span></span>` : ''}
          </button>`;
        });
        html += '</div>';

      } else if (this.selectedDate) {
        html += `<div class="booking-widget__empty">${this.t('no_slots', 'No available slots for this date.')}</div>`;
      }

      html += this.renderNotes();
      html += '</div>';

      this.innerHTML = html;
      this.bindEvents();
    }

    // Policy and bundle lines under the widget, on both screens. The policy is
    // read from the event's own settings, so an event that allows neither shows
    // nothing rather than a promise the manage-booking page will not keep.
    renderNotes() {
      const event = this.eventData;
      const notes = [];

      const policy = this.policyText(event);
      if (policy) {
        notes.push(`<p class="booking-widget__note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="booking-widget__note-icon booking-widget__note-icon--ok" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
          <span>${policy}</span>
        </p>`);
      }

      if (event.credit_enabled && this._t.bundle_url) {
        notes.push(`<p class="booking-widget__note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="booking-widget__note-icon booking-widget__note-icon--deal" aria-hidden="true"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r="1" fill="currentColor" stroke="none"/></svg>
          <span>${this.t('bundle_credit_text', 'Playroom credit is accepted for this booking.')} <a href="${this._t.bundle_url}">${this.t('bundle_credit_link', 'Save with a Playroom Bundle')}</a></span>
        </p>`);
      }

      return notes.length ? `<div class="booking-widget__notes">${notes.join('')}</div>` : '';
    }

    policyText(event) {
      const cancel = !!event.allow_cancel;
      const reschedule = !!event.allow_reschedule;
      if (!cancel && !reschedule) return null;

      const cancelHours = Number(event.cancel_lead_hours) || 0;
      const rescheduleHours = Number(event.reschedule_lead_hours) || 0;

      if (cancel && reschedule) {
        if (cancelHours !== rescheduleHours) {
          return this.t('policy_split', 'Cancel for free up to [cancel_hours] hours and reschedule up to [reschedule_hours] hours before the start.',
            { cancel_hours: cancelHours, reschedule_hours: rescheduleHours });
        }
        return cancelHours === 0
          ? this.t('policy_cancel_reschedule_anytime', 'Cancel or reschedule for free any time before the start.')
          : this.t('policy_cancel_reschedule', 'Cancel or reschedule for free up to [hours] hours before the start.', { hours: cancelHours });
      }
      if (cancel) {
        return cancelHours === 0
          ? this.t('policy_cancel_anytime', 'Cancel for free any time before the start.')
          : this.t('policy_cancel', 'Cancel for free up to [hours] hours before the start.', { hours: cancelHours });
      }
      return rescheduleHours === 0
        ? this.t('policy_reschedule_anytime', 'Reschedule for free any time before the start.')
        : this.t('policy_reschedule', 'Reschedule for free up to [hours] hours before the start.', { hours: rescheduleHours });
    }

    renderDetailsScreen() {
      const slot = this.selectedSlot;
      const event = this.eventData;
      const variant = this.variantData;
      const maxGuests = Math.floor(Math.min(event.capacity_per_slot, slot.capacity_remaining ?? event.capacity_per_slot) / variant.capacity_cost);
      const maxChildren = (this.guestCount * variant.capacity_cost) - 1;

      let html = '<div class="booking-widget">';

      // Back link (hidden when only one slot available)
      if (!this.singleSlotMode) {
        html += `<button class="booking-widget__back" data-action="go-slots">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          ${this.t('back_to_dates', 'Change date / time')}
        </button>`;
      }

      // Summary card
      html += `<div class="booking-widget__detail-header">
        <div class="booking-widget__detail-title">${event.name}${!this.privateEvent && variant.name && variant.name !== 'Default Title' && variant.name !== event.name ? ` &middot; ${variant.name}` : ''}</div>
        <div class="booking-widget__detail-meta">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          ${this.formatDateLong(this.selectedDate)} &middot; ${slot.start_time} – ${slot.end_time}
        </div>
        <div class="booking-widget__detail-meta">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          ${event.location.name}
        </div>
      </div>`;

      // Guest count — compact row
      html += `<div class="booking-widget__field">
        <div class="booking-widget__field-row">
          <span class="booking-widget__field-label">${this.eventData.guests_label || this.t('guests', 'Guests')}</span>
          <div class="booking-widget__counter">
            <button class="booking-widget__counter-btn" data-action="guests-dec" ${this.guestCount <= (event.min_guests || 1) ? 'disabled' : ''}>−</button>
            <span class="booking-widget__counter-value">${this.guestCount}</span>
            <button class="booking-widget__counter-btn" data-action="guests-inc" ${this.guestCount >= maxGuests ? 'disabled' : ''}>+</button>
            <span class="booking-widget__remove-child-placeholder"></span>
          </div>
        </div>
      </div>`;

      // Children ages (if age_required)
      if (event.age_required) {
        const { min: ageMin, max: ageMax } = this.ageRange();
        if (event.guests_are_children) {
          while (this.childrenAges.length < this.guestCount) this.childrenAges.push(this.defaultAge());
          this.childrenAges = this.childrenAges.slice(0, this.guestCount);
        }
        if (this.childrenAges.length === 0) this.childrenAges = [this.defaultAge()];

        html += '<div class="booking-widget__field">';
        html += '<div class="booking-widget__children">';

        this.childrenAges.forEach((age, i) => {
          html += `<div class="booking-widget__child-row">
            <span class="booking-widget__child-label">${this.t('child_age', 'Child [index]', { index: i + 1 })}</span>
            <div class="booking-widget__counter">
              <button class="booking-widget__counter-btn booking-widget__counter-btn--sm" data-action="age-dec" data-index="${i}" ${age <= ageMin ? 'disabled' : ''}>−</button>
              <span class="booking-widget__age-value"><span class="booking-widget__age-number">${age}</span><span class="booking-widget__age-unit">${age === 1 ? 'year' : 'years'}</span></span>
              <button class="booking-widget__counter-btn booking-widget__counter-btn--sm" data-action="age-inc" data-index="${i}" ${age >= ageMax ? 'disabled' : ''}>+</button>
              ${this.childrenAges.length > 1 && !event.guests_are_children
                ? `<button class="booking-widget__remove-child" data-action="remove-child" data-index="${i}">&times;</button>`
                : '<span class="booking-widget__remove-child-placeholder"></span>'}
            </div>
          </div>`;
        });

        if (!event.guests_are_children && this.childrenAges.length < maxChildren && maxChildren > 0) {
          html += `<button class="booking-widget__add-child" data-action="add-child">${this.t('add_child', '+ Add child')}</button>`;
        }

        html += '</div></div>';
      }

      // CTA
      const loadingClass = this.isLoading ? ' is-loading' : '';
      const cost = variant.capacity_cost || 1;
      const cartQty = Math.ceil(this.guestCount / cost);
      const unitPrice = (this.variantPrice || 0) / 100;
      const bookingTotal = cartQty * unitPrice;
      const fullCredit = this.credit && this.credit.credit >= bookingTotal && bookingTotal > 0;
      const partialCredit = this.credit && this.credit.credit > 0 && this.credit.credit < bookingTotal;

      if (fullCredit) {
        html += `<button class="booking-widget__cta booking-widget__cta--credit${loadingClass}" data-action="book-with-credit" ${this.isLoading ? 'disabled' : ''}>
          ${this.isLoading ? '' : this.t('credit_button', 'Use Playroom Credit')}
        </button>`;
        html += `<div class="booking-widget__credit-info">
          <span class="booking-widget__credit-badge">${this.t('credit_left', '[credit] left', { credit: this.formatPrice(this.credit.credit) })}</span>
          <span class="booking-widget__expiry-note">${this.t('credit_deducted', '[amount] will be deducted', { amount: this.formatPrice(bookingTotal) })}</span>
        </div>`;
      } else if (partialCredit) {
        const remaining = bookingTotal - this.credit.credit;
        html += `<button class="booking-widget__cta${loadingClass}" data-action="add-to-cart" ${this.isLoading ? 'disabled' : ''}>
          ${this.isLoading ? '' : this.t('book_now', 'Book Now')}
        </button>`;
        html += `<div class="booking-widget__credit-info">
          <span class="booking-widget__credit-badge">${this.t('credit_left', '[credit] left', { credit: this.formatPrice(this.credit.credit) })}</span>
          <span class="booking-widget__expiry-note">${this.t('credit_pay_rest', 'Credit applied at checkout. You pay [amount].', { amount: this.formatPrice(remaining) })}</span>
        </div>`;
      } else {
        html += `<button class="booking-widget__cta${loadingClass}" data-action="add-to-cart" ${this.isLoading ? 'disabled' : ''}>
          ${this.isLoading ? '' : this.t('book_now', 'Book Now')}
        </button>`;
        html += `<div class="booking-widget__expiry-note">${this.t('expiry_note', 'This reservation will expire if checkout is not completed within 15 minutes.')}</div>`;
      }

      html += this.renderNotes();
      html += '</div>';

      this.innerHTML = html;
      this.bindEvents();
    }

    renderCalendar() {
      if (!this.calendarMonth) {
        if (this.firstMonth) {
          this.calendarMonth = this.firstMonth;
        } else {
          const today = new Date();
          this.calendarMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        }
        this.loadCalendarMonth(this.calendarMonth);
        return '<div class="booking-widget__calendar"><div class="booking-widget__loading">Loading...</div></div>';
      }

      const [year, month] = this.calendarMonth.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      const startDow = (firstDay.getDay() + 6) % 7; // Monday = 0
      const locale = document.documentElement.lang || 'en';
      const monthName = firstDay.toLocaleString(locale, { month: 'long', year: 'numeric' });

      const canPrev = !this.firstMonth || this.calendarMonth > this.firstMonth;
      const canNext = !this.lastMonth || this.calendarMonth < this.lastMonth;

      let html = '<div class="booking-widget__calendar">';
      html += '<div class="booking-widget__calendar-header">';
      html += canPrev
        ? `<button class="booking-widget__calendar-nav" data-action="cal-prev">&lsaquo;</button>`
        : `<span class="booking-widget__calendar-nav" style="visibility:hidden">&lsaquo;</span>`;
      html += `<span class="booking-widget__calendar-title">${monthName}</span>`;
      html += canNext
        ? `<button class="booking-widget__calendar-nav" data-action="cal-next">&rsaquo;</button>`
        : `<span class="booking-widget__calendar-nav" style="visibility:hidden">&rsaquo;</span>`;
      html += '</div>';

      html += '<div class="booking-widget__calendar-grid">';
      const dowNames = [...Array(7)].map((_, i) => {
        const d = new Date(2026, 0, 5 + i); // Mon 5 Jan 2026
        return d.toLocaleString(locale, { weekday: 'narrow' });
      });
      dowNames.forEach(d => {
        html += `<div class="booking-widget__calendar-dow">${d}</div>`;
      });

      // Pad empty cells before day 1
      for (let i = 0; i < startDow; i++) {
        html += '<div class="booking-widget__calendar-day booking-widget__calendar-day--empty">&nbsp;</div>';
      }

      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      for (let d = 1; d <= lastDay.getDate(); d++) {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const isPast = dateStr < today;
        const hasSlots = !isPast && this.calendarSlots[dateStr] && this.calendarSlots[dateStr].length > 0;
        const isSelected = dateStr === this.selectedDate;
        const isToday = dateStr === today;

        let cls = 'booking-widget__calendar-day';
        if (isPast) cls += ' is-past';
        if (hasSlots) cls += ' has-slots';
        if (isSelected) cls += ' is-selected';
        if (isToday) cls += ' is-today';

        html += `<div class="${cls}" ${hasSlots ? `data-action="cal-select" data-date="${dateStr}" role="button" tabindex="0"` : ''}>${d}</div>`;
      }

      html += '</div></div>';
      return html;
    }

    // ──────── Events ────────

    bindEvents() {
      this.querySelectorAll('[data-action]').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const action = el.dataset.action;

          switch (action) {
            case 'select-date':
              this.selectedDate = el.dataset.date;
              this.selectedSlot = null;
              this.render();
              break;

            case 'select-slot':
              this.selectedSlot = JSON.parse(el.dataset.slot);
              this.screen = 'details';
              this.render();
              break;

            case 'toggle-calendar':
              this.calendarOpen = !this.calendarOpen;
              if (this.calendarOpen && !this.calendarMonth) {
                this.calendarMonth = null;
              }
              this.render();
              break;

            case 'cal-prev':
            case 'cal-next': {
              const [y, m] = this.calendarMonth.split('-').map(Number);
              const d = new Date(y, m - 1 + (action === 'cal-next' ? 1 : -1), 1);
              this.calendarMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              this.calendarSlots = {};
              this.render(); // show grid immediately (no slots highlighted yet)
              this.loadCalendarMonth(this.calendarMonth); // then re-render with slots
              break;
            }

            case 'cal-select':
              this.selectedDate = el.dataset.date;
              this.selectedSlot = null;
              if (this.calendarSlots[this.selectedDate]) {
                const existing = this.availableDays.find(d => d.date === this.selectedDate);
                if (!existing) {
                  this.availableDays.push({
                    date: this.selectedDate,
                    slots: this.calendarSlots[this.selectedDate]
                  });
                }
              }
              this.calendarOpen = false;
              this.render();
              break;

            case 'go-slots':
              this.screen = 'slots';
              this.render();
              break;

            case 'guests-inc': {
              const stepUp = this.variantData?.capacity_cost || 1;
              this.guestCount += stepUp;
              this.updateDisplayedPrice();
              this.render();
              break;
            }

            case 'guests-dec': {
              const stepDown = this.variantData?.capacity_cost || 1;
              const minGuests = this.eventData?.min_guests || stepDown;
              this.guestCount = Math.max(minGuests, this.guestCount - stepDown);
              const maxC = (this.guestCount * this.variantData.capacity_cost) - 1;
              if (!this.eventData?.guests_are_children && this.childrenAges.length > maxC) {
                this.childrenAges = this.childrenAges.slice(0, Math.max(0, maxC));
              }
              this.updateDisplayedPrice();
              this.render();
              break;
            }

            case 'age-inc': {
              const idx = parseInt(el.dataset.index);
              this.childrenAges[idx] = this.stepAge(this.childrenAges[idx], 1);
              this.render();
              break;
            }

            case 'age-dec': {
              const idx = parseInt(el.dataset.index);
              this.childrenAges[idx] = this.stepAge(this.childrenAges[idx], -1);
              this.render();
              break;
            }

            case 'add-child':
              this.childrenAges.push(this.defaultAge());
              this.render();
              break;

            case 'remove-child': {
              const idx = parseInt(el.dataset.index);
              this.childrenAges.splice(idx, 1);
              this.render();
              break;
            }

            case 'add-to-cart':
              this.reserveSlot();
              break;

            case 'book-with-credit':
              this.bookWithCredit();
              break;
          }
        });
      });
    }

    // ──────── Helpers ────────

    getSlotsForDate(dateStr) {
      if (!dateStr) return [];
      if (this.calendarSlots[dateStr]) return this.calendarSlots[dateStr];
      const day = this.availableDays.find(d => d.date === dateStr);
      return day ? day.slots : [];
    }

    formatDateShort(dateStr) {
      const d = new Date(dateStr + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (d.getTime() === today.getTime()) return this.t('today', 'Today');
      if (d.getTime() === tomorrow.getTime()) return this.t('tomorrow', 'Tomorrow');

      const locale = document.documentElement.lang || 'en';
      return d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
    }

    formatDateLong(dateStr) {
      const d = new Date(dateStr + 'T00:00:00');
      const locale = document.documentElement.lang || 'en';
      return d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }

    formatPrice(amount) {
      const n = parseFloat(amount);
      return n % 1 === 0 ? `€${n.toFixed(0)}` : `€${n.toFixed(2)}`;
    }
  });
}
